import { mix, sbg, SBG_HEX } from '../../theme/sbgTokens';

/**
 * Shared physical symbols for the seating view, the layout editor and the
 * printable plan. The floor reads like an architectural drawing in neutrals:
 * walls and room outlines in `border.strong`, open areas as dashed hairlines on
 * a subtle fill, landmarks as the one blue-tinted zone. Zones never take a
 * categorical color, so color on the plan stays a signal (selection, a
 * highlighted team) rather than a surface (book §6, §13.3).
 */
export interface FloorplanAreaAppearance {
  /** CSS fill for the area. */
  background: string;
  /** CSS border shorthand (`2px solid …`) for HTML renderers. */
  border: string;
  /** The border's parts, for SVG renderers. */
  stroke: string;
  strokeWidth: number;
  dashed: boolean;
  borderRadius: number;
  labelColor: string;
  labelAtTop: boolean;
  hideLabel: boolean;
}

/**
 * The one desk highlight for a picked team: a light Primary Blue wash inside a
 * 2px selected outline. One hue for any team, so it scales past eight teams.
 */
export const FLOORPLAN_TEAM_HIGHLIGHT = {
  fill: mix(sbg.action.primary, 16, sbg.bg.surface),
  border: `2px solid ${sbg.border.selected}`,
};

/** Every area type the editor can place, in the order they appear in legends and menus. */
export const FLOORPLAN_AREA_TYPES = ['office', 'meeting_room', 'open_area', 'landmark', 'room', 'wall', 'door'] as const;

const AREA_TYPE_LABELS: Record<string, string> = {
  office: 'Office',
  meeting_room: 'Meeting room',
  open_area: 'Open area',
  landmark: 'Landmark',
  room: 'Room',
  wall: 'Wall',
  door: 'Door',
};

/** "meeting_room" → "Meeting room". Unknown types fall back to sentence case. */
export const floorplanAreaTypeLabel = (type: string) =>
  AREA_TYPE_LABELS[type] ?? type.replace(/_/g, ' ').replace(/^./, (letter) => letter.toUpperCase());

const ROOM_TYPES = new Set(['office', 'room', 'meeting_room']);

type Medium = 'screen' | 'print';

interface AppearanceOptions {
  /** `print` returns literal light-theme hex for the standalone print document. */
  medium?: Medium;
}

const build = (
  parts: Omit<FloorplanAreaAppearance, 'border'>,
): FloorplanAreaAppearance => ({
  ...parts,
  border: `${parts.strokeWidth}px ${parts.dashed ? 'dashed' : 'solid'} ${parts.stroke}`,
});

const printAppearance = (type: string): FloorplanAreaAppearance => {
  if (type === 'wall') {
    return build({ background: SBG_HEX.slate400, stroke: SBG_HEX.slate400, strokeWidth: 1, dashed: false, borderRadius: 2, labelColor: SBG_HEX.white, labelAtTop: false, hideLabel: true });
  }
  if (type === 'door') {
    return build({ background: SBG_HEX.white, stroke: SBG_HEX.slate400, strokeWidth: 2, dashed: true, borderRadius: 2, labelColor: SBG_HEX.slate600, labelAtTop: false, hideLabel: true });
  }
  if (ROOM_TYPES.has(type)) {
    return build({ background: SBG_HEX.white, stroke: SBG_HEX.slate400, strokeWidth: 2, dashed: false, borderRadius: 6, labelColor: SBG_HEX.slate600, labelAtTop: true, hideLabel: false });
  }
  const isLandmark = type === 'landmark';
  return build({
    background: isLandmark ? SBG_HEX.blue50 : SBG_HEX.slate25,
    stroke: isLandmark ? SBG_HEX.blue : SBG_HEX.slate200,
    strokeWidth: 1,
    dashed: true,
    borderRadius: 4,
    labelColor: isLandmark ? SBG_HEX.navy : SBG_HEX.slate600,
    labelAtTop: false,
    hideLabel: false,
  });
};

export const getFloorplanAreaAppearance = (type: string, options: AppearanceOptions = {}): FloorplanAreaAppearance => {
  const { medium = 'screen' } = options;
  if (medium === 'print') return printAppearance(type);

  if (type === 'wall') {
    return build({
      background: sbg.border.strong,
      stroke: sbg.border.strong,
      strokeWidth: 1,
      dashed: false,
      borderRadius: 2,
      labelColor: sbg.text.secondary,
      labelAtTop: false,
      hideLabel: true,
    });
  }
  if (type === 'door') {
    return build({
      background: sbg.bg.surface,
      stroke: sbg.border.strong,
      strokeWidth: 2,
      dashed: true,
      borderRadius: 2,
      labelColor: sbg.text.secondary,
      labelAtTop: false,
      hideLabel: true,
    });
  }
  if (ROOM_TYPES.has(type)) {
    // Walled rooms: white floor, a solid strong outline, the name at the top.
    return build({
      background: sbg.bg.surface,
      stroke: sbg.border.strong,
      strokeWidth: 2,
      dashed: false,
      borderRadius: 6,
      labelColor: sbg.text.secondary,
      labelAtTop: true,
      hideLabel: false,
    });
  }
  if (type === 'landmark') {
    // The one tinted zone: a single selected-blue wash, as on the printed plan.
    return build({
      background: sbg.bg.selected,
      stroke: sbg.border.selected,
      strokeWidth: 1,
      dashed: true,
      borderRadius: 4,
      labelColor: sbg.text.accent,
      labelAtTop: false,
      hideLabel: false,
    });
  }
  // Open areas (and unknown zone types): a quiet subtle fill inside a dashed hairline.
  return build({
    background: sbg.bg.surfaceSubtle,
    stroke: sbg.border.default,
    strokeWidth: 1,
    dashed: true,
    borderRadius: 4,
    labelColor: sbg.text.secondary,
    labelAtTop: false,
    hideLabel: false,
  });
};

/**
 * Legend groups for the area types present on a floor. Offices, meeting rooms
 * and rooms share one symbol (their names are written on the plan), so they
 * share one legend entry.
 */
export const floorplanAreaLegendGroups = (types: readonly string[]): { key: string; label: string; appearance: FloorplanAreaAppearance }[] => {
  const groups: { key: string; label: string; appearance: FloorplanAreaAppearance }[] = [];
  const present = new Set(types.filter((type) => type !== 'wall' && type !== 'door'));
  const rooms = Array.from(present).filter((type) => ROOM_TYPES.has(type));
  if (rooms.length) {
    groups.push({
      key: 'rooms',
      label: rooms.length === 1 ? floorplanAreaTypeLabel(rooms[0]) : 'Offices and rooms',
      appearance: getFloorplanAreaAppearance(rooms[0]),
    });
  }
  ['open_area', 'landmark'].forEach((type) => {
    if (present.has(type)) groups.push({ key: type, label: floorplanAreaTypeLabel(type), appearance: getFloorplanAreaAppearance(type) });
  });
  Array.from(present)
    .filter((type) => !ROOM_TYPES.has(type) && type !== 'open_area' && type !== 'landmark')
    .forEach((type) => groups.push({ key: type, label: floorplanAreaTypeLabel(type), appearance: getFloorplanAreaAppearance(type) }));
  return groups;
};
